// Bord 5 als invoer (Spoor B, sessie B2): het oordeel van een mens.
//
// Vier van de vijf borden vullen zichzelf. Bord 5 niet, en dat is geen gebrek
// maar het punt ervan (ADR §3): of de top-25 grond geeft om een opleiding op
// te bouwen, en of de editie bruikbaar was, kan geen script beslissen. Twee
// meters zijn daarom invoerbaar — D1 en D2; de derde (D3, "wordt gebruikt")
// komt vanzelf zodra het plan-scherm draait.
//
// De invoer landt op de periode en de methode die je op dat moment BEKIJKT
// (`huidig`), niet op "vandaag": een oordeel over de stand van augustus hoort
// bij augustus, ook als je het in september invoert.
//
// Wat dit onderdeel nooit doet: een uitslag zelf uitrekenen of tonen. Na een
// geslaagde POST haalt het vlak de borden opnieuw op; wat je daarna leest komt
// van de server. Een 400 draagt een Nederlandse zin ("De teller kan niet
// groter zijn dan de noemer.") en die zin staat er letterlijk.

import { useId, useState } from "react";

import { api } from "../../lib/api";
import type { MeterkastSnede } from "../../contracts";
import { color, radius, size, weight } from "../../tokens";
import { invoer, knopDonker, knopLicht } from "./stijl";
import { leesbaar } from "./woorden";

/** Hoeveel trends de blinde ronde standaard beslaat (ADR §3, meter D1). Een
 *  greep kan korter zijn — daarom een veld en geen vast getal in de code. */
const TOP = 25;

export function Oordeel({
  meter,
  sector,
  huidig,
  onGewijzigd,
}: {
  meter: "D1" | "D2";
  sector: string;
  huidig: MeterkastSnede;
  onGewijzigd: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [ja, setJa] = useState("");
  const [van, setVan] = useState(String(TOP));
  const [toelichting, setToelichting] = useState("");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [gelukt, setGelukt] = useState<string | null>(null);
  const veldId = useId();

  async function leg(teller: number, noemer: number) {
    setBezig(true);
    setFout(null);
    setGelukt(null);
    try {
      const uit = await api.meterkastOordeel({
        meter,
        sector,
        teller,
        noemer,
        toelichting: toelichting.trim() || null,
        period: huidig.period,
        methode: huidig.methode,
      });
      // De bevestiging is de rij van de server, niet onze eigen invoer.
      setGelukt(
        `Vastgelegd onder ${uit.sector}, methode ${uit.methode}, periode ${uit.period}.`,
      );
      setJa("");
      setToelichting("");
      setOpen(false);
      onGewijzigd();
    } catch (err) {
      // `leesbaar` houdt de zin van de server bij een 400/404/422 ("De teller
      // kan niet groter zijn dan de noemer."); `err.reason` zou die vervangen.
      setFout(leesbaar(err, "Het oordeel kon niet worden vastgelegd."));
    } finally {
      setBezig(false);
    }
  }

  if (!open) {
    return (
      <div style={{ marginTop: 12 }}>
        <button type="button" style={knopLicht} onClick={() => setOpen(true)}>
          {meter === "D1" ? "Oordeel invoeren" : "Editie beoordelen"}
        </button>
        {gelukt && <Melding soort="goed">{gelukt}</Melding>}
        {fout && <Melding soort="fout">{fout}</Melding>}
      </div>
    );
  }

  const tellerGetal = Number(ja);
  const noemerGetal = Number(van);
  const geldig =
    meter === "D2" ||
    (ja.trim() !== "" &&
      Number.isFinite(tellerGetal) &&
      Number.isFinite(noemerGetal) &&
      noemerGetal > 0 &&
      tellerGetal >= 0 &&
      tellerGetal <= noemerGetal);

  return (
    <form
      data-oordeel={meter}
      onSubmit={(e) => {
        e.preventDefault();
        if (meter === "D1" && geldig) void leg(tellerGetal, noemerGetal);
      }}
      style={{
        marginTop: 12,
        padding: 14,
        border: `1px solid ${color.border}`,
        borderRadius: radius.block,
        background: color.surface,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        maxWidth: 520,
      }}
    >
      <p style={{ margin: 0, fontSize: size.meta, color: color.muted, lineHeight: 1.55 }}>
        {meter === "D1"
          ? `Blind gelezen: bij hoeveel van de sterkste trends luidt het oordeel "hier is een opleiding op te bouwen of aan te passen"?`
          : `Is de editie bruikbaar geweest?`}
        {` Dit oordeel gaat naar ${sector}, methode ${huidig.methode}${
          huidig.period ? `, periode ${huidig.period}` : ""
        }.`}
      </p>

      {meter === "D1" && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          <label style={{ display: "block" }}>
            <span style={veldLabel}>Aantal "ja"</span>
            <input
              id={`${veldId}-ja`}
              type="number"
              min={0}
              max={noemerGetal || undefined}
              step={1}
              value={ja}
              onChange={(e) => setJa(e.target.value)}
              style={{ ...invoer, width: 110 }}
            />
          </label>
          <label style={{ display: "block" }}>
            <span style={veldLabel}>Van hoeveel trends</span>
            <input
              id={`${veldId}-van`}
              type="number"
              min={1}
              step={1}
              value={van}
              onChange={(e) => setVan(e.target.value)}
              style={{ ...invoer, width: 110 }}
            />
          </label>
        </div>
      )}

      <label style={{ display: "block" }}>
        <span style={veldLabel}>Toelichting (mag leeg)</span>
        <textarea
          id={`${veldId}-toelichting`}
          rows={2}
          value={toelichting}
          onChange={(e) => setToelichting(e.target.value)}
          style={{ ...invoer, resize: "vertical" }}
        />
      </label>

      {fout && <Melding soort="fout">{fout}</Melding>}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {meter === "D1" ? (
          <button type="submit" disabled={bezig || !geldig} style={knopDonker(bezig || !geldig)}>
            {bezig ? "Bezig…" : "Vastleggen"}
          </button>
        ) : (
          <>
            <button
              type="button"
              disabled={bezig}
              style={knopDonker(bezig)}
              onClick={() => void leg(1, 1)}
            >
              Bruikbaar
            </button>
            <button
              type="button"
              disabled={bezig}
              style={knopLicht}
              onClick={() => void leg(0, 1)}
            >
              Niet bruikbaar
            </button>
          </>
        )}
        <button type="button" style={knopLicht} onClick={() => setOpen(false)} disabled={bezig}>
          Annuleren
        </button>
      </div>
    </form>
  );
}

function Melding({ soort, children }: { soort: "goed" | "fout"; children: string }) {
  return (
    <p
      role={soort === "fout" ? "alert" : undefined}
      data-oordeel-melding={soort}
      style={{
        margin: "8px 0 0",
        fontSize: size.meta,
        lineHeight: 1.55,
        color: soort === "fout" ? color.accentRed : color.accentGreen,
      }}
    >
      {children}
    </p>
  );
}

const veldLabel = {
  display: "block",
  marginBottom: 6,
  fontSize: size.label,
  fontWeight: weight.medium,
  color: color.faint,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
} as const;
